import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Paper,
} from "@mui/material";
import { Column } from "../../redux/slice/commonSlice";
import { tokens } from "../../theme";

type Section = {
  columns: Column[];
  rows: any[];
  isStrikeThrough?: boolean
};

type CustomMultipleTableProps = {
  sections: Section[];
};

const CustomMultipleTable: React.FC<CustomMultipleTableProps> = ({ sections }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const visibleSections = sections.filter((section) => section.rows?.length > 0);

  if (!visibleSections.length) {
    return null;
  }

  if (isMobile) {
    // 📱 Mobile: rows as cards, dashed line before each section (except first)
    return (
      <Box>
        {visibleSections.map((section, sIndex) => (
          <Box key={sIndex}>
            {/* dashed separator (not for first section) */}
            {sIndex > 0 && (
              <Box
                sx={{
                  borderTop: `1px dashed ${colors.grey[600]}`,
                  my: 2,
                }}
              />
            )}

            {section.rows.map((row, rIndex) => (
              <Paper
                key={rIndex}
                 sx={{
                  p: 2,
                  mb: 2,
                  border: "none",
                  // border: `1px solid ${colors.grey[700]}`,
                  backgroundColor: colors.grey[900],
                  borderRadius: 3,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 14px rgba(0,0,0,0.4)",
                  },
                }}
              >
                {section.columns.map((col, idx) => (
                  <Box
                    key={col.id}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    py={1}
                    // borderBottom={
                    //   idx < section.columns.length - 1
                    //     ? `1px solid ${colors.grey[800]}`
                    //     : "none"
                    // }
                    gap={2}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: "#22C55E",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "0.8rem",
                        flexShrink: 0,
                      }}
                    >
                      {col.label}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 500,
                        color: colors.grey[100],
                        textAlign: "right",
                        ...(section.isStrikeThrough ? {
                          textDecoration: "line-through red",
                          textDecorationThickness: "2px",
                        } : {}),
                      }}
                    >
                      {row[col.id]}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  // 🖥️ Desktop: keep grouped tables
  return (
    <TableContainer sx={{ background: colors.grey[900], boxShadow: "none" }}>
      <Table>
        {visibleSections.map((section, sIndex) => (
          <React.Fragment key={sIndex}>
            <TableHead>
              <TableRow
              >
                {section.columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align ?? "left"}
                    sx={{
                      fontWeight: "bold",
                      color: "#22C55E",
                      backgroundColor: theme.palette.mode === "dark"? "#272c3452" :"#e4e4e454",
                      minWidth: col.minWidth,
                      border : "none"
                    }}
                  >
                    <Typography fontWeight="bold">
                      {col.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {section.rows.map((row, rIndex) => (
                <TableRow
                  key={rIndex}
                >
                  {section.columns.map((col) => (
                    <TableCell key={col.id} align={col.align ?? "left"} sx={{ border: "none" }}>
                      <Typography
                        variant="body1"
                        sx={ section.isStrikeThrough ? {
                          textDecoration: "line-through red",
                          textDecorationThickness: "2px"
                        } : {}}
                      >
                        {row[col.id]}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {sIndex < visibleSections.length - 1 && (
                <TableRow>
                  <TableCell
                    colSpan={section.columns.length}
                    sx={{
                      p: 0,
                      height: 12,
                      backgroundColor: theme.palette.mode === "dark"? "#03112146" :"#e4e4e454",
                      borderBottom: 0,
                      pb:3
                    }}
                  />
                </TableRow>
              )}
            </TableBody>
          </React.Fragment>
        ))}
      </Table>
    </TableContainer>
  );
};

export default CustomMultipleTable;
